// GtImportWizard — the one CSV import on the platform.
// C1-CLIENTIMPORT: entity 'clients' imports clients and their contacts. What
// is required, the wording and whether a destination is offered all come from
// the server's adapter, so the wizard stays one screen for every entity.
//
// Every import (candidates now, clients next) runs through this screen and the
// server's /api/imports engine. Steps: choose a file, match its columns to our
// fields, then check every row before anything is saved. The check table
// follows the bench table: sentence-case headings, grey row numbers, square
// avatars, bold names, one line per row.
//
// An import never sends or notifies anyone. It creates new records, matches
// people already in the pool (never overwriting them) and can place everyone
// on a bench bucket or a role stage in the same step.
//
// Props:
//   entity      'candidates' (the server lists what is supported)
//   target      optional starting destination { kind: 'bench', bench_id, stage_id } | { kind: 'role', role_id }
//   lockTarget  true keeps the destination fixed to that bench (bucket can still be chosen) or role
//   authFetch, flash, onClose, onDone(result), openRecord(ref) optional
import { useState, useEffect, useMemo, useRef } from 'react';
import { GtSelect } from './GtControls';
import { parseCsv, autoMap, applyMapping } from './gtCsv';
import { GtEnrichModal } from './GtEnrich';

const NEW = '__new';
const CUSTOM = '__custom'; // C1-CLIENTFACTS: keep a column as a new field
const SKIP = '';
const BUCKET_TONES = ['teal', 'green', 'blue', 'slate', 'purple'];
const PAGE_SIZES = [25, 50, 100];
const STATUS = {
  new: { label: 'New', cls: 'gtim-st--new' },
  existing: { label: 'In your pool', cls: 'gtim-st--existing' },
  duplicate: { label: 'Duplicate', cls: 'gtim-st--dup' },
  skipped: { label: 'Skipped', cls: 'gtim-st--skip' },
};
const CHIPS = [
  { key: 'all', label: 'All rows' },
  { key: 'new', label: 'New' },
  { key: 'existing', label: 'Already in your pool' },
  { key: 'duplicate', label: 'Duplicates' },
  { key: 'skipped', label: 'Skipped' },
];
const json = { 'Content-Type': 'application/json' };

function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }
function plural(n, one, many) { return n.toLocaleString('en-GB') + ' ' + (n === 1 ? one : many); }
function sizeLabel(bytes) { return bytes < 1024 * 1024 ? Math.max(1, Math.round(bytes / 1024)) + ' KB' : (bytes / (1024 * 1024)).toFixed(1) + ' MB'; }

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return; }
  const t = document.createElement('textarea');
  t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
  document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
}

export default function GtImportWizard({ entity = 'candidates', target = null, lockTarget = false, authFetch, flash, onClose, onDone, openRecord }) {
  const [step, setStep] = useState(1);
  const [spec, setSpec] = useState(null);
  const [file, setFile] = useState(null);
  const [csv, setCsv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(''); // C1-IMPORTBATCH: 'Checking 2,000 of 8,000 rows'
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const checkWrap = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  // Destination
  const [kind, setKind] = useState(target ? target.kind : 'none');
  const [benches, setBenches] = useState(null);
  const [benchId, setBenchId] = useState(target && target.kind === 'bench' ? target.bench_id : '');
  const [newBench, setNewBench] = useState('');
  const [buckets, setBuckets] = useState([]);
  // null until chosen; '' means no bucket.
  const [bucketId, setBucketId] = useState(target && target.kind === 'bench' && target.stage_id !== undefined ? (target.stage_id == null ? '' : target.stage_id) : null);
  const [newBucket, setNewBucket] = useState('');
  const [roles, setRoles] = useState(null);
  const [roleId, setRoleId] = useState(target && target.kind === 'role' ? target.role_id : '');
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState('');

  // Check table
  const [chip, setChip] = useState('all');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  useEffect(() => {
    authFetch('/api/imports/fields?entity=' + encodeURIComponent(entity))
      .then(setSpec)
      .catch(e => setError(e.message || 'Could not load the import fields.'));
  }, [authFetch, entity]);

  useEffect(() => {
    if (kind !== 'bench' || benches !== null) return;
    authFetch('/api/benches?status=active')
      .then(r => {
        const list = r.benches || [];
        setBenches(list);
        setBenchId(cur => (cur !== '' ? cur : list.length ? list[0].id : NEW));
      })
      .catch(() => { setBenches([]); setBenchId(NEW); });
  }, [authFetch, kind, benches]);

  useEffect(() => {
    if (kind !== 'bench') return;
    if (!benchId || benchId === NEW) { setBuckets([]); setBucketId(NEW); return; }
    let live = true;
    authFetch('/api/benches/' + benchId)
      .then(r => {
        if (!live) return;
        const b = r.buckets || [];
        setBuckets(b);
        setBucketId(cur => (cur === '' || (cur !== null && cur !== NEW && b.some(x => String(x.id) === String(cur))) ? cur : b.length ? b[0].id : NEW));
      })
      .catch(() => { if (live) { setBuckets([]); setBucketId(NEW); } });
    return () => { live = false; };
  }, [authFetch, kind, benchId]);

  useEffect(() => {
    if (kind !== 'role' || roles !== null) return;
    authFetch('/api/roles')
      .then(r => {
        const list = (Array.isArray(r) ? r : (r.roles || [])).filter(x => ['filled', 'closed'].indexOf(x.status) === -1);
        list.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setRoles(list);
      })
      .catch(() => setRoles([]));
  }, [authFetch, kind, roles]);

  useEffect(() => {
    if (kind !== 'role' || !roleId) { setStages([]); setStageId(''); return; }
    let live = true;
    authFetch('/api/roles/' + roleId + '/stages')
      .then(r => {
        if (!live) return;
        const st = (r.stages || []).filter(s => s.outcome === 'open' && s.side !== 'client');
        setStages(st);
        const entry = st.find(s => s.key === 'sourced') || st.find(s => s.is_entry) || st[0];
        setStageId(entry ? entry.id : '');
      })
      .catch(() => { if (live) setStages([]); });
    return () => { live = false; };
  }, [authFetch, kind, roleId]);

  const fields = useMemo(() => (spec ? spec.fields : []), [spec]);
  const fieldByKey = useMemo(() => {
    const o = {}; fields.forEach(f => { o[f.key] = f; }); return o;
  }, [fields]);
  const maxRows = spec ? spec.max_rows : 5000;
  const noun = spec ? spec.label : 'records';

  // ── Step 1: the file ──
  async function takeFile(f) {
    setError('');
    if (!f) return;
    if (!/\.(csv|tsv|txt)$/i.test(f.name)) { setError('That is not a CSV file. Export the list as CSV and try again.'); return; }
    if (f.size > 20 * 1024 * 1024) { setError('That file is over 20 MB. Split it and import it in parts.'); return; }
    try {
      const parsed = parseCsv(await f.text());
      if (!parsed.headers.length || !parsed.rows.length) { setError('The file has no rows under its headings.'); return; }
      if (parsed.rows.length > maxRows) {
        setError('The file has ' + plural(parsed.rows.length, 'row', 'rows') + '. Up to ' + maxRows.toLocaleString('en-GB') + ' can go in at a time. Split it and import it in parts.');
        return;
      }
      setFile({ name: f.name, size: f.size });
      setCsv(parsed);
      setMapping(autoMap(parsed.headers, fields));
      setCheck(null);
      setStep(2);
    } catch (e) { setError('The file could not be read.'); }
  }

  // ── Step 2: columns ──
  const samples = useMemo(() => {
    if (!csv) return [];
    return csv.headers.map((h, i) => {
      const out = [];
      for (let r = 0; r < csv.rows.length && out.length < 3; r += 1) {
        const v = String(csv.rows[r][i] == null ? '' : csv.rows[r][i]).trim();
        if (v && out.indexOf(v) === -1) out.push(v);
      }
      return out;
    });
  }, [csv]);
  const mappedKeys = Object.keys(mapping).map(i => mapping[i]).filter(Boolean);
  const need = (spec && spec.need) || { any: ['full_name', 'first_name'], message: 'Match a column to Full name or First name. Rows without a name are skipped.' };
  const hasName = need.any.some(k => mappedKeys.indexOf(k) !== -1);
  const fieldOpts = [{ value: SKIP, label: 'Do not import' }].concat(fields.map(f => ({ value: f.key, label: f.label })),
    spec && spec.custom_fields ? [{ value: CUSTOM, label: '+ Add as a new field' }] : []);

  function setColumn(i, key) {
    setMapping(m => {
      const next = { ...m };
      if (!key) { delete next[i]; return next; }
      const f = fieldByKey[key];
      // A field takes one column unless it joins several (City, Country into Location).
      if (f && !f.joiner) Object.keys(next).forEach(k => { if (next[k] === key) delete next[k]; });
      next[i] = key;
      return next;
    });
  }

  // Columns kept as new fields travel as _custom: { heading: value }.
  const rowsOut = useMemo(() => {
    if (!csv) return [];
    const plain = {};
    const extra = [];
    Object.keys(mapping).forEach(i => { if (mapping[i] === CUSTOM) extra.push(Number(i)); else plain[i] = mapping[i]; });
    const out = applyMapping(csv.rows, plain, fields);
    if (!extra.length) return out;
    return out.map((o, r) => {
      const c = {};
      extra.forEach(i => { const v = String(csv.rows[r][i] == null ? '' : csv.rows[r][i]).trim(); if (v) c[csv.headers[i]] = v; });
      return Object.keys(c).length ? { ...o, _custom: c } : o;
    });
  }, [csv, mapping, fields]);

  // C1-IMPORTBATCH: big files go up in parts, so no single request runs long enough to time out.
  const batch = (spec && spec.batch) || 1000;
  async function runCheck() {
    setBusy(true); setError('');
    try {
      const all = { counts: { total: 0, new: 0, existing: 0, duplicate: 0, skipped: 0 }, summary: null, rows: [] };
      for (let k = 0; k < rowsOut.length; k += batch) {
        if (rowsOut.length > batch) setProgress('Checking ' + Math.min(k + batch, rowsOut.length).toLocaleString('en-GB') + ' of ' + rowsOut.length.toLocaleString('en-GB') + ' rows…');
        const r = await authFetch('/api/imports/preview', { method: 'POST', headers: json, body: JSON.stringify({ entity, rows: rowsOut.slice(k, k + batch) }) });
        Object.keys(all.counts).forEach((c) => { all.counts[c] += (r.counts && r.counts[c]) || 0; });
        all.summary = rowsOut.length > batch ? null : r.summary;
        (r.rows || []).forEach((x) => all.rows.push({ ...x, row: x.row + k }));
      }
      setCheck(all); setChip('all'); setPage(0); setStep(3);
    } catch (e) { setError(e.message === 'Failed to fetch' ? 'The connection dropped while checking the file. Try again.' : (e.message || 'The file could not be checked.')); }
    setBusy(false); setProgress('');
  }

  // ── Step 3: destination and import ──
  async function resolveTarget() {
    if (kind === 'none') return { kind: 'none' };
    if (kind === 'role') return { kind: 'role', role_id: Number(roleId), stage_id: stageId ? Number(stageId) : null };
    let bid = benchId;
    let current = buckets;
    if (bid === NEW) {
      const r = await authFetch('/api/benches', { method: 'POST', headers: json, body: JSON.stringify({ name: newBench.trim() }) });
      bid = r.bench.id; current = [];
      setBenches(null); setBenchId(bid);
    }
    let sid = bucketId;
    if (sid === NEW) {
      const label = newBucket.trim();
      const r = await authFetch('/api/benches/' + bid + '/buckets', {
        method: 'PUT', headers: json,
        body: JSON.stringify({ buckets: current.map(b => ({ id: b.id, label: b.label, tone: b.tone }))
          .concat([{ label, tone: BUCKET_TONES[current.length % BUCKET_TONES.length] }]) }),
      });
      const made = (r.buckets || []).find(b => b.label === label);
      sid = made ? made.id : null;
      setBuckets(r.buckets || []); setBucketId(sid); setNewBucket('');
    }
    return { kind: 'bench', bench_id: Number(bid), stage_id: sid === '' || sid == null ? null : Number(sid) };
  }

  async function runImport() {
    setBusy(true); setError('');
    try {
      const t = await resolveTarget();
      const named = {};
      Object.keys(mapping).forEach(i => { named[csv.headers[i]] = mapping[i]; });
      // One import, sent in parts; each part is saved as it lands.
      let sum = null;
      let importId = null;
      let doneRows = 0;
      try {
        for (let k = 0; k < rowsOut.length; k += batch) {
          const part = rowsOut.slice(k, k + batch);
          const last = k + batch >= rowsOut.length;
          if (rowsOut.length > batch) setProgress('Importing ' + Math.min(k + batch, rowsOut.length).toLocaleString('en-GB') + ' of ' + rowsOut.length.toLocaleString('en-GB') + ' rows…');
          const r = await authFetch('/api/imports/commit', {
            method: 'POST', headers: json,
            body: JSON.stringify({ entity, rows: part, filename: file.name, headers: csv.headers, mapping: named, target: t, import_id: importId, final: last }),
          });
          importId = r.import.id;
          doneRows = k + part.length;
          if (!sum) sum = { ...r };
          else for (const f of ['created', 'matched', 'duplicates', 'skipped', 'placed', 'already_there']) sum[f] = (sum[f] || 0) + (r[f] || 0);
          if (rowsOut.length > batch) sum.summary = null;
        }
      } catch (e) {
        if (!sum) throw e;
        const why = e.message === 'Failed to fetch' ? 'the connection dropped' : e.message;
        setError('The first ' + doneRows.toLocaleString('en-GB') + ' rows were imported, then ' + why + '. Import the file again: people already imported are matched, not added twice.');
      }
      setResult(sum);
      if (onDone) onDone(sum);
    } catch (e) { setError(e.message === 'Failed to fetch' ? 'The connection dropped before anything was imported. Nothing was changed. Try again.' : (e.message || 'The import did not go through. Nothing was changed.')); }
    setBusy(false); setProgress('');
  }

  const counts = check ? check.counts : null;
  // Into the pool only, matched people need nothing doing; elsewhere they are placed too.
  const toSave = !counts ? 0 : kind === 'none' ? counts.new : counts.new + counts.existing;
  const destMissing = (kind === 'bench' && ((benchId === NEW && !newBench.trim()) || (bucketId === NEW && !newBucket.trim()) || benchId === '' || bucketId === null))
    || (kind === 'role' && !roleId);

  const filtered = useMemo(() => {
    if (!check) return [];
    return chip === 'all' ? check.rows : check.rows.filter(r => r.status === chip);
  }, [check, chip]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const shown = filtered.slice(safePage * pageSize, (safePage * pageSize) + pageSize);

  // Shade the edge that has more columns past it, as the bench table does.
  function measure() {
    const el = checkWrap.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdges(e => (e.left === left && e.right === right ? e : { left, right }));
  }
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  function destinationLabel() {
    if (kind === 'none') return 'the pool';
    if (kind === 'bench') {
      const b = (benches || []).find(x => String(x.id) === String(benchId));
      const k = buckets.find(x => String(x.id) === String(bucketId));
      const bName = benchId === NEW ? newBench.trim() || 'a new bench' : b ? b.name : 'the bench';
      const kName = bucketId === NEW ? newBucket.trim() || 'a new bucket' : k ? k.label : null;
      return kName ? bName + ', ' + kName : bName;
    }
    const r = (roles || []).find(x => String(x.id) === String(roleId));
    return r ? r.title : 'the role';
  }

  function notesFor(r) {
    // Problems first: they are what needs a look.
    const bits = [];
    (r.problems || []).forEach(p => { const t = /[.]$/.test(p) ? p : p + '.'; if (bits.indexOf(t) === -1) bits.push(t); });
    if (r.reason) { const t = r.reason + '.'; if (bits.indexOf(t) === -1) bits.push(t); }
    if (r.status === 'existing' && r.match) bits.push('Matched by ' + r.matchedBy + '. Their record is left as it is.');
    return bits.join(' ');
  }

  async function copyRef(ref) {
    try { await copyText(ref); flash('Copied ' + ref); } catch (e) { flash('Could not copy. Select the ID and copy it by hand.', true); }
  }

  const benchOpts = (benches || []).map(b => ({ value: b.id, label: b.name + (b.client_name ? ' · ' + b.client_name : '') }))
    .concat(lockTarget ? [] : [{ value: NEW, label: '+ A new bench' }]);
  const bucketOpts = [{ value: '', label: 'No bucket' }].concat(buckets.map(b => ({ value: b.id, label: b.label })), [{ value: NEW, label: '+ A new bucket' }]);
  const kindOpts = [
    { value: 'none', label: 'The pool only' },
    { value: 'bench', label: 'A bench' },
    { value: 'role', label: 'A role' },
  ];

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtim-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head gtim-head">
          <span>Import {noun} from a CSV file</span>
          {!result && (
            <ol className="gtim-steps" aria-label="Import steps">
              {['File', 'Columns', 'Check and import'].map((s, i) => (
                <li key={s} className={step === i + 1 ? 'gtim-step--on' : step > i + 1 ? 'gtim-step--done' : ''}>
                  <span className="gtim-stepno">{step > i + 1 ? '✓' : i + 1}</span>{s}
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="gtc-modal-body gtim-body">
          {error && <div className="gtnt-banner gtnt-banner--amber gtim-error">{error}</div>}

          {result ? (
            <div className="gtim-result">
              <div className="gtnt-banner gtnt-banner--green">
                <b>{file.name} imported.</b>{' '}
                {result.summary ? result.summary : <>
                {plural(result.created, 'new record', 'new records')} created, {entity === 'candidates' ? plural(result.matched, 'person', 'people') + ' already in your pool' : plural(result.matched, 'existing record', 'existing records')} matched
                {result.target.kind !== 'none' && <>, {plural(result.placed, 'person', 'people')} added to {result.target.kind === 'bench' ? result.target.name : result.target.title}</>}.
                {result.already_there > 0 && ' ' + plural(result.already_there, 'person was', 'people were') + ' already there and were left where they are.'}
                {(result.duplicates > 0 || result.skipped > 0) && ' ' + [result.duplicates ? plural(result.duplicates, 'duplicate row', 'duplicate rows') : null, result.skipped ? plural(result.skipped, 'row', 'rows') + (entity === 'candidates' ? ' with no name' : ' with no company') : null].filter(Boolean).join(' and ') + ' left out.'}
                </>}
                {result.summary && (result.duplicates > 0 || result.skipped > 0) && ' ' + [result.duplicates ? plural(result.duplicates, 'duplicate row', 'duplicate rows') : null, result.skipped ? plural(result.skipped, 'row', 'rows') + ' with no company' : null].filter(Boolean).join(' and ') + ' left out.'}
              </div>
              <div className="gtim-resultmeta">
                <span>Import</span>
                <button type="button" className="gtbn-idcopy" onClick={() => copyRef(result.import.ref)} aria-label={'Copy ID ' + result.import.ref}>
                  <span className="gtbn-idtext">{result.import.ref}</span><span className="gtbn-idicon" aria-hidden="true">⧉</span>
                </button>
                <span className="gtim-dot">·</span>
                <span>Nobody was contacted.</span>
              </div>
            </div>
          ) : !spec ? (
            <div className="gtr-empty-sm">{error ? '' : 'Loading…'}</div>
          ) : step === 1 ? (
            <>
              <button type="button"
                className={'gtim-drop' + (dragOver ? ' gtim-drop--over' : '')}
                onClick={() => fileInput.current && fileInput.current.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
                <span className="gtim-dropicon" aria-hidden="true">⇪</span>
                <b>Drop a CSV file here, or click to choose one</b>
                <span>Exports from Apollo, LinkedIn, Sales Navigator, Clay, Lusha or any spreadsheet. Up to {maxRows.toLocaleString('en-GB')} rows.</span>
              </button>
              <input ref={fileInput} type="file" accept=".csv,.tsv,.txt,text/csv" className="gtim-hidden"
                onChange={e => { takeFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
              <ul className="gtim-promises">
                <li>Nothing is saved until you have checked every row.</li>
                <li>{spec.match_rule || 'People already in your pool are matched by email, LinkedIn, or name and company. Their records are never overwritten.'}</li>
                <li>An import never emails or notifies anyone.</li>
              </ul>
            </>
          ) : step === 2 ? (
            <>
              <div className="gtim-filebar">
                <span className="gtim-fileicon" aria-hidden="true">CSV</span>
                <span className="gtim-filename">{file.name}</span>
                <span className="gtbn-muted">{plural(csv.rows.length, 'row', 'rows')} · {plural(csv.headers.length, 'column', 'columns')} · {sizeLabel(file.size)}</span>
                <button type="button" className="gtc-btn-ghost gtim-change" onClick={() => { setStep(1); setCsv(null); setFile(null); }}>Choose another file</button>
              </div>
              <p className="gtbn-help">We matched {plural(Object.keys(mapping).length, 'column', 'columns')} for you. Check each one, and set any column you do not need to “Do not import”.{spec.custom_fields ? ' Anything else worth keeping can go in with “Add as a new field”.' : ''}</p>
              {!hasName && <div className="gtnt-banner gtnt-banner--amber">{need.message}</div>}
              <div className="gtbn-tableframe">
                <div className="gtbn-tablewrap">
                  <table className="gtbn-table gtim-maptable">
                    <colgroup><col style={{ width: 56 }} /><col style={{ width: 220 }} /><col /><col style={{ width: 240 }} /></colgroup>
                    <thead>
                      <tr><th>#</th><th>Column in your file</th><th>Sample values</th><th>Imports into</th></tr>
                    </thead>
                    <tbody>
                      {csv.headers.map((h, i) => (
                        <tr key={i} className={mapping[i] ? '' : 'gtim-tr--off'}>
                          <td><span className="gtim-rowno">{i + 1}</span></td>
                          <td><b className="gtbn-clip" title={h}>{h}</b></td>
                          <td>{samples[i].length ? <span className="gtbn-clip gtbn-muted" title={samples[i].join(' · ')}>{samples[i].join(' · ')}</span> : <span className="gtbn-muted">Empty</span>}</td>
                          <td><GtSelect value={mapping[i] || SKIP} onChange={v => setColumn(i, v)} options={fieldOpts} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="gtim-cards">
                {CHIPS.map(c => {
                  const n = c.key === 'all' ? counts.total : counts[c.key];
                  return (
                    <button key={c.key} type="button"
                      className={'gtim-count gtim-count--' + c.key + (chip === c.key ? ' gtim-count--on' : '')}
                      onClick={() => { setChip(c.key); setPage(0); }} aria-pressed={chip === c.key}>
                      <b>{n.toLocaleString('en-GB')}</b><span>{(spec.chip_labels && spec.chip_labels[c.key]) || c.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="gtim-dest">
                {spec.destinations !== false && <div className="gtim-dest-row">
                  <div className="gtc-field gtim-dest-kind"><span>Add everyone to</span>
                    {lockTarget ? <div className="gtim-locked">{kind === 'bench' ? ((benches || []).find(x => String(x.id) === String(benchId)) || { name: 'This bench' }).name : destinationLabel()}</div>
                      : <GtSelect value={kind} onChange={v => { setKind(v); setError(''); }} options={kindOpts} />}
                  </div>
                  {kind === 'bench' && !lockTarget && (
                    <div className="gtc-field gtbn-grow"><span>Bench</span>
                      {benches === null ? <div className="gtr-empty-sm">Loading benches…</div> : <GtSelect value={benchId} onChange={setBenchId} options={benchOpts} />}
                    </div>
                  )}
                  {kind === 'bench' && benchId === NEW && (
                    <label className="gtc-field gtbn-grow"><span>New bench name</span>
                      <input value={newBench} maxLength={120} placeholder="e.g. Net New AI Developers" onChange={e => setNewBench(e.target.value)} />
                    </label>
                  )}
                  {kind === 'bench' && (
                    <div className="gtc-field gtbn-grow"><span>Bucket</span>
                      <GtSelect value={bucketId} onChange={setBucketId} options={bucketOpts} />
                    </div>
                  )}
                  {kind === 'bench' && bucketId === NEW && (
                    <label className="gtc-field gtbn-grow"><span>New bucket name</span>
                      <input value={newBucket} maxLength={60} placeholder="e.g. From Apollo" onChange={e => setNewBucket(e.target.value)} />
                    </label>
                  )}
                  {kind === 'role' && !lockTarget && (
                    <div className="gtc-field gtbn-grow"><span>Role</span>
                      {roles === null ? <div className="gtr-empty-sm">Loading roles…</div> : roles.length === 0
                        ? <div className="gtr-empty-sm">There are no open roles.</div>
                        : <GtSelect value={roleId} onChange={setRoleId} placeholder="Choose a live role" options={roles.map(r => ({ value: r.id, label: r.title + (r.client_name ? ' · ' + r.client_name : '') }))} />}
                    </div>
                  )}
                  {kind === 'role' && roleId && (
                    <div className="gtc-field gtbn-grow"><span>Stage</span>
                      <GtSelect value={stageId} onChange={setStageId} placeholder="Choose a stage" options={stages.map(s => ({ value: s.id, label: s.label }))} />
                    </div>
                  )}
                </div>}
                {check.summary ? <p className="gtbn-help">{check.summary} Nobody is contacted.</p> : <p className="gtbn-help">
                  {counts.new > 0 ? plural(counts.new, 'new record', 'new records') + ' will be created. ' : 'Nobody new will be created. '}
                  {counts.existing > 0 ? (entity === 'candidates' ? plural(counts.existing, 'person', 'people') + ' already in your pool' : plural(counts.existing, 'existing record', 'existing records')) + ' will be matched and left as they are. ' : ''}
                  {kind !== 'none' && toSave > 0 ? 'All ' + plural(toSave, 'person', 'people') + ' go to ' + destinationLabel() + '. ' : ''}
                  Nobody is contacted.
                </p>}
              </div>

              {shown.length === 0 ? (
                <div className="gtr-emptytab">No rows in this view.</div>
              ) : (
                <>
                  <div className={'gtbn-tableframe' + (edges.left ? ' gtbn-edge-l' : '') + (edges.right ? ' gtbn-edge-r' : '')}>
                    <div className="gtbn-tablewrap" ref={checkWrap} onScroll={measure}>
                      <table className="gtbn-table gtim-checktable" style={{ minWidth: 1530 }}>
                        <colgroup>
                          <col style={{ width: 250 }} /><col style={{ width: 230 }} /><col style={{ width: 170 }} /><col style={{ width: 160 }} />
                          <col style={{ width: 200 }} /><col style={{ width: 190 }} /><col style={{ width: 160 }} /><col style={{ width: 300 }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="gtbn-pin"><div className="gtbn-pinrow"><span className="gtim-pin-row">Row</span><span className="gtbn-pin-name">Name</span></div></th>
                            <th>Status</th><th>Title</th><th>Company</th><th>Email</th><th>LinkedIn</th><th>Location</th><th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shown.map(r => {
                            const d = r.display || {};
                            const st = spec.status_labels && spec.status_labels[r.status] ? { ...STATUS[r.status], label: spec.status_labels[r.status] } : STATUS[r.status];
                            const notes = notesFor(r);
                            return (
                              <tr key={r.row} className={r.status === 'duplicate' || r.status === 'skipped' ? 'gtim-tr--off' : ''}>
                                <td className="gtbn-pin">
                                  <div className="gtbn-pinrow">
                                    <span className="gtim-pin-row"><span className="gtim-rowno">{r.row + 1}</span></span>
                                    <span className="gtbn-pin-name">
                                      <span className="gtbn-av" style={{ background: d.name ? avatarColour(d.name) : '#CBD5E1' }}>{initials(d.name)}</span>
                                      {d.name ? <b className="gtbn-clip" title={d.name}>{d.name}</b> : <span className="gtbn-muted">No name</span>}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className={'gtim-st ' + st.cls}>{st.label}</span>
                                  {r.status === 'existing' && r.match && r.match.ref && (
                                    <button type="button" className="gtbn-idcopy gtim-matchid"
                                      onClick={() => (openRecord ? openRecord(r.match.ref) : copyRef(r.match.ref))}
                                      title={openRecord ? 'Open ' + r.match.ref : 'Copy this ID'} aria-label={(openRecord ? 'Open ' : 'Copy ID ') + r.match.ref}>
                                      <span className="gtbn-idtext">{r.match.ref}</span>
                                    </button>
                                  )}
                                </td>
                                <td>{d.title ? <span className="gtbn-clip" title={d.title}>{d.title}</span> : <span className="gtbn-muted">—</span>}</td>
                                <td>{d.company ? <span className="gtbn-clip" title={d.company}>{d.company}</span> : <span className="gtbn-muted">—</span>}</td>
                                <td>{d.email ? <span className="gtbn-clip" title={d.email}>{d.email}</span> : <span className="gtbn-muted">—</span>}</td>
                                <td>{d.linkedin ? <a className="gtbn-clip gtim-link" href={d.linkedin} target="_blank" rel="noreferrer" title={d.linkedin}>{d.linkedin.replace(/^https?:\/\/(www\.)?/, '')}</a> : <span className="gtbn-muted">—</span>}</td>
                                <td>{d.location ? <span className="gtbn-clip" title={d.location}>{d.location}</span> : <span className="gtbn-muted">—</span>}</td>
                                <td>{notes ? <span className={'gtbn-clip' + ((r.problems || []).length ? ' gtim-warn' : ' gtbn-muted')} title={notes}>{notes}</span> : <span className="gtbn-muted">—</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="gtbn-pager">
                    <div className="gtbn-pager-size">
                      <span>Rows per page</span>
                      <div className="gtbn-pager-select"><GtSelect value={pageSize} onChange={v => { setPageSize(Number(v)); setPage(0); }} options={PAGE_SIZES.map(n => ({ value: n, label: String(n) }))} /></div>
                    </div>
                    <div className="gtbn-pager-nav">
                      <button type="button" className="gtc-btn-ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</button>
                      <b>{safePage + 1} of {pages}</b>
                      <button type="button" className="gtc-btn-ghost" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}>Next</button>
                    </div>
                    <div className="gtbn-pager-count">{((safePage * pageSize) + 1) + '–' + ((safePage * pageSize) + shown.length) + ' of ' + filtered.length}</div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="gtc-modal-foot">
          {result ? (
            <>
              {result.created > 0 && entity === 'candidates' && (
                <button className="gtc-btn-ghost" onClick={() => setEnrichOpen(true)}>Enrich the {result.created === 1 ? 'new person' : result.created + ' new people'}</button>
              )}
              <button className="gtc-primary" onClick={onClose}>Done</button>
            </>
          ) : step === 1 ? (
            <button className="gtc-btn-ghost" onClick={onClose}>Cancel</button>
          ) : step === 2 ? (
            <>
              <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
              <button className="gtc-primary" disabled={busy || !hasName} onClick={runCheck}>{busy ? (progress || 'Checking…') : 'Check ' + plural(csv.rows.length, 'row', 'rows')}</button>
            </>
          ) : (
            <>
              <button className="gtc-btn-ghost" disabled={busy} onClick={() => { setStep(2); setError(''); }}>Back to columns</button>
              <button className="gtc-primary" disabled={busy || !toSave || destMissing} onClick={runImport}>
                {busy ? (progress || 'Importing…') : !toSave ? 'Nothing new to import' : kind === 'none' ? 'Import ' + plural(toSave, entity === 'clients' ? 'row' : 'new record', entity === 'clients' ? 'rows' : 'new records') : (counts.new ? 'Import and add ' : 'Add ') + plural(toSave, 'person', 'people')}
              </button>
            </>
          )}
        </div>
        {enrichOpen && result && (
          <GtEnrichModal importId={result.import.id} authFetch={authFetch} flash={flash} allowDeep={false}
            onClose={() => setEnrichOpen(false)} onStarted={() => setEnrichOpen(false)} />
        )}
      </div>
    </div>
  );
}
